import type { FC, ReactNode } from 'react'

import { AppWithMonitoring } from '@skytech/monitoring'
import { PageLoader } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { checkLocaleAgainstTranslations, DEFAULT_LOCALE, getBrowserLocale } from '@skytech/utils'
import { QueryClientProvider, useQuery } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill'
import { IntlProvider } from 'react-intl'

import InjuryReportForm from '@/forms/injury-report'
import OffboardingForm from '@/forms/offboarding'
import OnboardingForm from '@/forms/onboarding'

import { Header } from './Header'
import { AppContextProvider, useAppContext } from './index.context'
import queries from './queries'
import { queryClient } from './queryClient'
import appResources from './resources'
import Text from './translations/Text'

import './index.styles.scss'

const AppIntl: FC<{ children: ReactNode }> = ({ children }) => {
  const { locale, setLocale } = useAppContext()

  const { data: translations, isLoading: isTranslationsLoading } = useQuery(
    queries.translationList(),
    appResources.getTranslationList,
    {
      staleTime: Infinity,
      onSuccess(translationList) {
        if (!translationList) return
        const browserLocale = getBrowserLocale()
        const updatedLocale = checkLocaleAgainstTranslations({
          locale: browserLocale,
          translationList,
          defaultLocale: DEFAULT_LOCALE,
        })
        setLocale(updatedLocale)
      },
    }
  )

  if (isTranslationsLoading) return <PageLoader />
  if (!translations)
    return <div className="form-app-init-load-error">Could not load translations.</div>

  return (
    <IntlProvider
      locale={locale}
      defaultLocale={DEFAULT_LOCALE}
      messages={translations[locale]}
      // Do not show missing translation errors
      onError={() => {}}
    >
      {children}
    </IntlProvider>
  )
}

const AppCore: FC = () => {
  polyfillCountryFlagEmojis()
  const { data, isLoading: isFormLoading } = useQuery(queries.form(), appResources.getForm)

  if (isFormLoading) return <PageLoader />

  if (!data)
    return (
      <div className="form-app-init-load-error">
        <Text id="Could not load form." />
      </div>
    )

  const { formType } = data

  return (
    <div className="form-app">
      <Header />

      <main>
        {formType === 'PORTING' && <OnboardingForm />}
        {formType === 'TERMINATION' && <OffboardingForm />}
        {formType === 'INJURY_REPORT' && <InjuryReportForm />}
      </main>

      {/* For testing sentry error reports */}
      {process.env.VERCEL_ENV !== 'production' && (
        <div className="trigger-sentry-error-button">
          <Button
            variant="primary"
            onClick={() => {
              throw Error('Sentry test')
            }}
          >
            Trigger sentry error
          </Button>
        </div>
      )}
    </div>
  )
}

export const App: FC = () => (
  <AppWithMonitoring>
    <QueryClientProvider client={queryClient}>
      <AppContextProvider>
        <AppIntl>
          <AppCore />
        </AppIntl>
      </AppContextProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </AppWithMonitoring>
)
